import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { TargetDataType } from './programBank';

/*
 * Target data points — per-session values collected for a client's
 * program targets. Server: [server/src/routes/target_data_points.js].
 *
 * Lifecycle:
 *   - Bulk POST at session save: one request per session, array of points.
 *   - PATCH to amend a single entered value (clinical correction).
 *   - DELETE to remove a point entered in error (audit row captures it).
 *
 * The server enforces UNIQUE (target_id, session_id) — don't POST a second
 * time for the same pair. Amendments must PATCH.
 */

export interface TargetDataPoint {
    id: string;
    clientProgramTargetId: string;
    sessionId: string;
    dataType: TargetDataType;
    valueNumeric: number;
    valueJson: TargetDataPointValueJson | null;
    notes: string | null;
    collectedBy: string | null;
    collectedAt: string;
}

// Trial + task_analysis store an array of configured result-code strings
// (see trial_result_codes table + migration 67). The server computes
// value_numeric from each code's is_correct flag at insert time, so the
// client ships only the codes.
export type TargetDataPointValueJson =
    | { trials: string[] }
    | { correct: number; total: number }
    | { stepResults: Array<{ index: number; code: string }> }
    | Record<string, unknown>;

export interface NewDataPoint {
    clientProgramTargetId: string;
    // Optional for trial/task_analysis (server recomputes from codes) but
    // required for percent/frequency/duration.
    valueNumeric?: number;
    valueJson?: TargetDataPointValueJson | null;
    notes?: string | null;
    collectedAt?: string;
}

interface ListOpts {
    targetId: string;
    dateFrom?: string;
    dateTo?: string;
}

async function fetchTargetDataPoints(opts: ListOpts): Promise<TargetDataPoint[]> {
    const params: Record<string, string> = { targetId: opts.targetId };
    if (opts.dateFrom) params.dateFrom = opts.dateFrom;
    if (opts.dateTo)   params.dateTo   = opts.dateTo;
    const res = await api.get<TargetDataPoint[]>('/target-data-points', { params });
    return res.data;
}

export function useTargetDataPoints(opts: ListOpts, enabled = true) {
    return useQuery({
        queryKey: ['target-data-points', opts.targetId, opts.dateFrom ?? '', opts.dateTo ?? ''] as const,
        queryFn: () => fetchTargetDataPoints(opts),
        enabled: enabled && !!opts.targetId,
        staleTime: 30_000,
    });
}

/**
 * Fetch the single data point (if any) for this (target, session) pair.
 * Used by the in-session data-collection UI to hydrate prior values when a
 * clinician re-opens a session that's already been saved.
 *
 * Filters client-side on a one-day window around the session date; the
 * UNIQUE (target_id, session_id) constraint server-side guarantees at most
 * one hit.
 */
export async function fetchDataPointForSession(
    targetId: string,
    sessionId: string,
): Promise<TargetDataPoint | null> {
    const rows = await fetchTargetDataPoints({ targetId });
    return rows.find((r) => r.sessionId === sessionId) ?? null;
}

export function useSaveDataPoints() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: { sessionId: string; points: NewDataPoint[] }) => {
            const res = await api.post<TargetDataPoint[]>('/target-data-points', payload);
            return res.data;
        },
        onSuccess: (rows) => {
            const targetIds = new Set(rows.map((r) => r.clientProgramTargetId));
            for (const targetId of targetIds) {
                qc.invalidateQueries({ queryKey: ['target-data-points', targetId] });
            }
        },
    });
}

export interface UpdateDataPointPayload {
    valueNumeric?: number;
    valueJson?: TargetDataPointValueJson | null;
    notes?: string | null;
    collectedAt?: string;
}

export function useUpdateDataPoint() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: UpdateDataPointPayload }) => {
            const res = await api.patch<TargetDataPoint>(`/target-data-points/${id}`, payload);
            return res.data;
        },
        onSuccess: (row) => {
            qc.invalidateQueries({ queryKey: ['target-data-points', row.clientProgramTargetId] });
        },
    });
}

export function useDeleteDataPoint() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, targetId }: { id: string; targetId: string }) => {
            await api.delete(`/target-data-points/${id}`);
            return targetId;
        },
        onSuccess: (targetId) => {
            qc.invalidateQueries({ queryKey: ['target-data-points', targetId] });
        },
    });
}
